'use client';

import { Transition } from '@headlessui/react';
import { PlusIcon, SwatchIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $patchStyleText } from '@lexical/selection';
import { $getRoot, $getSelection, $isRangeSelection } from 'lexical';

/* ---------- color utils ---------- */
function hexToRgb(hex) {
    let r = 0,
        g = 0,
        b = 0;
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
    }
    return { r, g, b };
}
function rgbToHsl({ r, g, b }) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    let h = 0,
        s;
    const l = (max + min) / 2;
    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    return { h: h * 360, s, l };
}
function hslToRgb({ h, s, l }) {
    let r, g, b;
    if (s === 0) {
        r = g = b = l;
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h / 360 + 1 / 3);
        g = hue2rgb(p, q, h / 360);
        b = hue2rgb(p, q, h / 360 - 1 / 3);
    }
    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255),
    };
}
function rgbToHex({ r, g, b }) {
    return (
        '#' +
        [r, g, b]
            .map((x) => {
                const hx = Number(x).toString(16);
                return hx.length === 1 ? '0' + hx : hx;
            })
            .join('')
    );
}

export function appendGradientColor(
    colors,
    primaryColor = '#3b82f6',
    secondaryColor = '#10b981'
) {
    if (!colors.length) return [primaryColor];
    if (colors.length === 1) return [...colors, secondaryColor];

    const last = colors[colors.length - 1] || primaryColor;
    const lastHsl = rgbToHsl(hexToRgb(last));
    const nextColor = rgbToHex(
        hslToRgb({
            h: (lastHsl.h + 45) % 360,
            s: Math.max(lastHsl.s, 0.5),
            l: Math.min(Math.max(lastHsl.l, 0.4), 0.7),
        })
    );
    return [...colors, nextColor];
}

function rgbStringToHex(rgbString) {
    const m = rgbString?.match?.(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (!m) return rgbString;
    return rgbToHex({ r: m[1], g: m[2], b: m[3] });
}
function getCssVarHex(varName, fallback = '#3b82f6') {
    if (typeof window === 'undefined') return fallback;
    const raw = getComputedStyle(document.documentElement).getPropertyValue(
        varName
    );
    const val = raw.trim();
    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(val) ? val : fallback;
}
function parseStyleString(styleStr = '') {
    const out = {};
    if (!styleStr) return out;
    styleStr.split(';').forEach((pair) => {
        const [k, v] = pair.split(':').map((s) => s && s.trim());
        if (k && v) out[k] = v;
    });
    return out;
}

function splitCssLayers(value = '') {
    const layers = [];
    let depth = 0;
    let start = 0;
    for (let index = 0; index < value.length; index += 1) {
        if (value[index] === '(') depth += 1;
        else if (value[index] === ')') depth = Math.max(0, depth - 1);
        else if (value[index] === ',' && depth === 0) {
            layers.push(value.slice(start, index).trim());
            start = index + 1;
        }
    }
    layers.push(value.slice(start).trim());
    return layers.filter(Boolean);
}

function getUnderlineBackground(selection) {
    if (!$isRangeSelection(selection)) return null;
    for (const node of selection.getNodes()) {
        if (typeof node.getStyle !== 'function') continue;
        const styles = parseStyleString(node.getStyle());
        const gradient = styles['border-image-source'];
        const thickness = parseFloat(styles['text-decoration-thickness'] || '');
        if (gradient && Number.isFinite(thickness)) {
            const position =
                splitCssLayers(styles['background-position'] || '')[0] ||
                'left calc(50% + 0.37em + 0px)';
            return { gradient, thickness, position };
        }
    }
    return null;
}

function underlineBackgroundStyles(underline, textGradient = '') {
    if (!underline) return null;
    const hasTextGradient = Boolean(textGradient);
    return {
        'background-image': hasTextGradient
            ? `${underline.gradient}, ${textGradient}`
            : underline.gradient,
        'background-size': hasTextGradient
            ? `100% ${underline.thickness}px, 100% 100%`
            : `100% ${underline.thickness}px`,
        'background-position': hasTextGradient
            ? `${underline.position}, left top`
            : underline.position,
        'background-repeat': hasTextGradient
            ? 'no-repeat, no-repeat'
            : 'no-repeat',
        '-webkit-background-clip': hasTextGradient
            ? 'border-box, text'
            : 'border-box',
        'background-clip': hasTextGradient ? 'border-box, text' : 'border-box',
    };
}

/* ---------- panel view ---------- */
export function ColorPanel({
    isOpen,
    colors,
    angle,
    setAngle,
    addColor,
    removeColor,
    updateColor,
    handleHexInputChange,
    handleRemoveColor,
    handleApplyColor,
    removeLabel = 'Remove',
    angleInputId = 'gradient-angle',
    gradientAngleOffset = 0,
    headerControls = null,
    extraControls = null,
}) {
    const aNum = Math.max(0, Math.min(360, Number(angle) || 0));
    const gradientCss =
        colors.length > 1
            ? `linear-gradient(${
                  (aNum + gradientAngleOffset) % 360
              }deg, ${colors.join(', ')})`
            : colors[0] || '#ffffff';

    const addButtonRef = useRef(null);
    const prevLen = useRef(colors.length);
    useEffect(() => {
        if (colors.length < prevLen.current) addButtonRef.current?.focus();
        prevLen.current = colors.length;
    }, [colors.length]);

    return (
        <Transition
            show={isOpen}
            appear
            enter="transition-all ease-out duration-200"
            enterFrom="opacity-0 -translate-y-2 max-h-0"
            enterTo="opacity-100 translate-y-0 max-h-72"
            leave="transition-all ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 max-h-72"
            leaveTo="opacity-0 -translate-y-2 max-h-0"
        >
            <style>{`
        .mdx-swatch-rail { -ms-overflow-style: none; scrollbar-width: none; }
        .mdx-swatch-rail::-webkit-scrollbar { display: none; }
      `}</style>

            <div className="space-y-2">
                {headerControls}
                {/* Row 1 — swatches (scroll) + actions (right) */}
                <div className="flex items-center gap-2">
                    <div className="mdx-swatch-rail min-w-0 flex-1 overflow-x-auto">
                        <div className="inline-flex items-center gap-2 pr-1">
                            {colors.map((color, i) => (
                                <div
                                    key={i}
                                    className="flex h-[26px] items-stretch overflow-hidden rounded-md border border-gray-300 bg-white"
                                    title={`Color ${i + 1}`}
                                >
                                    {/* color patch (flush) */}
                                    <div className="relative h-full w-[18px]">
                                        <div
                                            className="h-full w-full rounded-l-md"
                                            style={{ backgroundColor: color }}
                                        />
                                        <input
                                            type="color"
                                            value={color}
                                            onChange={(e) =>
                                                updateColor(i, e.target.value)
                                            }
                                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                            aria-label={`Pick color ${i + 1}`}
                                        />
                                    </div>

                                    {/* hex input */}
                                    <input
                                        type="text"
                                        value={color}
                                        onChange={(e) =>
                                            handleHexInputChange(e, i)
                                        }
                                        style={{ color }}
                                        className="h-full w-[56px] border-none bg-white px-1 text-center font-mono text-[10px] outline-none focus:ring-0"
                                        aria-label={`Hex for color ${i + 1}`}
                                    />

                                    {colors.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeColor(i)}
                                            className="h-full px-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                                            title="Remove color"
                                        >
                                            <XMarkIcon className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            ))}

                            {/* + add */}
                            <button
                                ref={addButtonRef}
                                type="button"
                                onClick={addColor}
                                className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border border-dashed border-gray-300 bg-white/50 text-gray-400 hover:border-gray-400 hover:text-gray-500"
                                title="Add color"
                            >
                                <PlusIcon className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>

                    {/* actions */}
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            type="button"
                            className="flex h-[23px] items-center rounded-lg border border-neutral-700 bg-white/50 px-2 text-xs text-neutral-700 hover:bg-neutral-100"
                            onClick={handleRemoveColor}
                        >
                            {removeLabel}
                        </button>
                        <button
                            type="button"
                            className="flex h-[23px] items-center rounded-lg bg-primary-700 px-2 text-xs text-white hover:bg-primary-800"
                            onClick={handleApplyColor}
                        >
                            Apply
                        </button>
                    </div>
                </div>

                {/* Row 2 — angle + preview */}
                {colors.length > 1 && (
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-[26px] flex-none items-center gap-1.5">
                            <label
                                htmlFor={angleInputId}
                                className="text-[11px] font-medium text-neutral-700"
                            >
                                Angle
                            </label>
                            <input
                                id={angleInputId}
                                type="number"
                                min="0"
                                max="360"
                                value={aNum}
                                onChange={(e) => {
                                    const n = Number(e.target.value);
                                    const clamped = Number.isFinite(n)
                                        ? Math.max(0, Math.min(360, n))
                                        : 0;
                                    setAngle(clamped);
                                }}
                                className="mdx-angle-input h-[22px] w-12 rounded-lg border border-gray-300 bg-white p-1 text-center font-mono text-[10px] text-primary-700 outline-none focus:ring-1 focus:ring-primary-500"
                            />
                        </div>

                        <div
                            className="h-[22px] min-w-[100px] flex-1 rounded-lg border border-gray-200"
                            style={{ background: gradientCss }}
                        />
                    </div>
                )}
                {extraControls}
            </div>
        </Transition>
    );
}

/* ---------- toolbar button + wiring ---------- */
export function TextColorButton({
    panelHostRef,
    open,
    onRequestToggle,
    onMarkdownSync,
    onRestoreSelection,
    panelAfter = null,
    hideButton = false,
}) {
    const [editor] = useLexicalComposerContext();

    const [colors, setColors] = useState(['#3b82f6']);
    const [angle, setAngle] = useState(45);

    const primaryHexRef = useRef('#3b82f6');
    const secondaryHexRef = useRef('#10b981');
    useEffect(() => {
        primaryHexRef.current = getCssVarHex('--primary-700', '#1B7CE4');
        secondaryHexRef.current = getCssVarHex('--secondary-700', '#1AB261');
    }, []);

    // reset when closed
    useEffect(() => {
        if (!open) {
            setColors([primaryHexRef.current]);
            setAngle(45);
        }
    }, [open]);

    const expandSelectionToAllIfNone = () => {
        let sel = $getSelection();
        if (!$isRangeSelection(sel) || sel.isCollapsed()) {
            const root = $getRoot();
            root.select(0, root.getTextContentSize());
            sel = $getSelection();
        }
        return sel && $isRangeSelection(sel) ? sel : null;
    };

    const applyColorsToSelection = (cols, ang) => {
        const valid = cols.filter((c) => /^#([0-9a-f]{3}){1,2}$/i.test(c));
        if (valid.length === 0) return;

        const aNum = Math.max(0, Math.min(360, Number(ang) || 0));
        const selection = $getSelection();
        const underline = getUnderlineBackground(selection);

        if (valid.length === 1) {
            $patchStyleText(selection, {
                ...(underlineBackgroundStyles(underline) || {
                    'background-image': null,
                    'background-size': null,
                    'background-position': null,
                    'background-repeat': null,
                    '-webkit-background-clip': null,
                    'background-clip': null,
                }),
                color: valid[0],
                '--editor-text-gradient-angle': null,
            });
        } else {
            const gradient = `linear-gradient(${
                (aNum + 90) % 360
            }deg, ${valid.join(', ')})`;
            $patchStyleText(selection, {
                ...(underlineBackgroundStyles(underline, gradient) || {
                    'background-image': gradient,
                    '-webkit-background-clip': 'text',
                    'background-clip': 'text',
                }),
                color: 'transparent',
                '--editor-text-gradient-angle': String(aNum),
            });
        }
    };

    // auto-apply palette tweaks when nothing is selected (applies to all text)
    const autoApplyIfNoSelection = (nextColors, nextAngle) => {
        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const sel = $getSelection();
            const shouldAuto = !$isRangeSelection(sel) || sel.isCollapsed();
            if (!shouldAuto) return;
            const expanded = expandSelectionToAllIfNone();
            if (!expanded) return;
            applyColorsToSelection(nextColors, nextAngle);
        });
    };

    // color controls
    const addColor = () => {
        setColors((prev) => {
            const next = appendGradientColor(
                prev,
                primaryHexRef.current,
                secondaryHexRef.current
            );
            autoApplyIfNoSelection(next, angle);
            return next;
        });
    };

    const removeColor = (idx) => {
        setColors((prev) => {
            if (prev.length <= 1) return prev;
            const next = prev.filter((_, i) => i !== idx);
            autoApplyIfNoSelection(next, angle);
            return next;
        });
    };

    const updateColor = (idx, newColor) => {
        setColors((prev) => {
            const next = [...prev];
            next[idx] = newColor;
            autoApplyIfNoSelection(next, angle);
            return next;
        });
    };

    const handleHexInputChange = (e, idx) => {
        const sanitized = e.target.value.replace(/[^0-9a-fA-F]/g, '');
        const newHex = '#' + sanitized.slice(0, 6);
        updateColor(idx, newHex);
    };

    const prefillFromSelection = () => {
        editor.update(() => onRestoreSelection?.());
        editor.getEditorState().read(() => {
            const sel = $getSelection();
            if (!$isRangeSelection(sel)) return;

            const nodes = sel.getNodes();
            let styleStr = '';
            for (const n of nodes) {
                if (typeof n.getStyle === 'function') {
                    const s = n.getStyle();
                    if (s) {
                        styleStr = s;
                        break;
                    }
                }
            }
            if (!styleStr) return;

            const styles = parseStyleString(styleStr);
            if (styles['color'] && styles['color'] !== 'transparent') {
                setColors([rgbStringToHex(styles['color'])]);
                setAngle(45);
                return;
            }
            const rawBackground =
                styles['background-image'] || styles['backgroundImage'];
            const bg = styles['border-image-source']
                ? splitCssLayers(rawBackground).at(-1)
                : rawBackground;
            if (bg && bg.includes('linear-gradient')) {
                const angleMatch = bg.match(/(-?\d+(?:\.\d+)?)deg/);
                const savedAngle = Number(
                    styles['--editor-text-gradient-angle']
                );
                const cssAngle = angleMatch ? parseFloat(angleMatch[1]) : 135;
                const a = Number.isFinite(savedAngle)
                    ? savedAngle
                    : (cssAngle + 270) % 360;
                const rgbColors =
                    bg.match(/rgb\([^)]+\)|#[0-9a-fA-F]{3,6}/g) || [];
                const hexes = rgbColors.map(rgbStringToHex);
                if (hexes.length > 0) {
                    setColors(hexes);
                    setAngle(isFinite(a) ? Math.max(0, Math.min(360, a)) : 45);
                }
            }
        });
    };

    const handleToggleOpen = (e) => {
        e.preventDefault();
        if (!open) prefillFromSelection();
        onRequestToggle?.();
    };

    const handleApplyColor = () => {
        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const expanded = expandSelectionToAllIfNone();
            if (!expanded) return;
            applyColorsToSelection(colors, angle);
        });
        onMarkdownSync?.();
    };

    const handleRemoveColor = () => {
        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const expanded = expandSelectionToAllIfNone();
            if (!expanded) return;
            const selection = $getSelection();
            const underline = getUnderlineBackground(selection);
            $patchStyleText(selection, {
                ...(underlineBackgroundStyles(underline) || {
                    'background-image': null,
                    'background-size': null,
                    'background-position': null,
                    'background-repeat': null,
                    '-webkit-background-clip': null,
                    'background-clip': null,
                }),
                color: null,
                '--editor-text-gradient-angle': null,
            });
        });
        onMarkdownSync?.();
    };

    return (
        <>
            {!hideButton && (
                <button
                    type="button"
                    className="mdx-btn"
                    data-active={open ? 'true' : undefined}
                    title="Set text color or gradient"
                    aria-label="Set text color or gradient"
                    onMouseDown={handleToggleOpen}
                >
                    <SwatchIcon
                        className="h-[14px] w-[14px]"
                        aria-hidden="true"
                    />
                </button>
            )}

            {panelHostRef?.current
                ? createPortal(
                      <Transition
                          show={open}
                          appear
                          enter="transition-all ease-out duration-200"
                          enterFrom="opacity-0 -translate-y-2 max-h-0"
                          enterTo="opacity-100 translate-y-0 max-h-[48rem]"
                          leave="transition-all ease-in duration-150"
                          leaveFrom="opacity-100 translate-y-0 max-h-[48rem]"
                          leaveTo="opacity-0 -translate-y-2 max-h-0"
                      >
                          <div className="mdx-panel-block space-y-3">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                  Text Color
                              </div>
                              <ColorPanel
                                  isOpen={true}
                                  colors={colors}
                                  angle={angle}
                                  setAngle={(v) => {
                                      setAngle(
                                          v
                                      ); /* live-apply if no selection */
                                  }}
                                  addColor={addColor}
                                  removeColor={removeColor}
                                  updateColor={updateColor}
                                  handleHexInputChange={handleHexInputChange}
                                  handleRemoveColor={handleRemoveColor}
                                  handleApplyColor={handleApplyColor}
                                  gradientAngleOffset={90}
                              />
                              {typeof panelAfter === 'function'
                                  ? panelAfter({ angle, setAngle })
                                  : panelAfter}
                          </div>
                      </Transition>,
                      panelHostRef.current
                  )
                : null}
        </>
    );
}
